import React, { useEffect, useMemo, useState } from "react"
import { MdAdd, MdDeleteOutline, MdEmail, MdSend } from "react-icons/md"
import { toast } from "react-toastify"
import Modal from "../index"
import Button from "../../Form/Button"
import FormGroup from "../../Form/FormGroup"
import Input from "../../Form/Input"
import api from "../../../services/api"
import styles from "./modalTransactions.module.css"

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const ModalFinancialEmail = ({ accountId, documentType, onSubmit, closeModal }) => {
    const [recipients, setRecipients] = useState([])
    const [selected, setSelected] = useState([])
    const [extraEmail, setExtraEmail] = useState("")
    const [loading, setLoading] = useState(true)
    const label = documentType === "invoice" ? "Nota Fiscal" : "cobrança"
    const hasValidExtraEmail = emailPattern.test(extraEmail.trim().toLowerCase())

    useEffect(() => {
        api.get(`/accounts/${accountId}/financial-email-recipients`)
            .then(response => {
                const values = response.data?.data || []
                setRecipients(values)
                setSelected(values.filter(item => item.default_selected).map(item => item.email))
            })
            .catch(error => toast.error(error?.response?.data?.message || "Não foi possível carregar os destinatários."))
            .finally(() => setLoading(false))
    }, [accountId])

    const selectedRecipients = useMemo(() => recipients.filter(recipient => selected.includes(recipient.email)), [recipients, selected])

    const toggleRecipient = email => setSelected(previous => previous.includes(email) ? previous.filter(item => item !== email) : [...previous, email])
    const removeExtraRecipient = email => {
        setRecipients(previous => previous.filter(recipient => recipient.email !== email || recipient.source_type !== "manual"))
        setSelected(previous => previous.filter(item => item !== email))
    }

    const addExtraEmail = () => {
        const email = extraEmail.trim().toLowerCase()
        if (!emailPattern.test(email)) return toast.error("Informe um e-mail válido.")
        if (recipients.some(item => item.email === email)) {
            setSelected(previous => previous.includes(email) ? previous : [...previous, email])
            setExtraEmail("")
            return
        }
        setRecipients(previous => [...previous, { email, name: email, source_type: "manual", source_label: "Somente neste envio" }])
        setSelected(previous => [...previous, email])
        setExtraEmail("")
    }

    const submit = event => {
        event.preventDefault()
        if (!selectedRecipients.length) return toast.error("Selecione pelo menos um destinatário.")
        onSubmit(selectedRecipients)
    }

    return <Modal icon={<MdEmail />} title={`Enviar ${label}`} description="Escolha entre os contatos cadastrados na conta. Endereços adicionais serão usados somente neste envio." closeModal={closeModal} fitContent={true}>
        <form className={styles.emailForm} onSubmit={submit}>
            <div className={styles.recipientList}>
                {loading && <div className={styles.emailEmpty}>Carregando destinatários...</div>}
                {!loading && !recipients.length && <div className={styles.emailEmpty}>Nenhum e-mail cadastrado. Adicione um endereço abaixo.</div>}
                {recipients.map(recipient => <div key={recipient.email} className={`${styles.recipientCard} ${selected.includes(recipient.email) ? styles.recipientSelected : ""}`}>
                    <button type="button" className={styles.recipientToggle} onClick={() => toggleRecipient(recipient.email)}>
                        <span className={styles.recipientCheck}>{selected.includes(recipient.email) ? "✓" : ""}</span>
                        <span><strong>{recipient.name || recipient.email}</strong><small>{recipient.email}</small><small>{recipient.source_label}</small></span>
                    </button>
                    {recipient.source_type === "manual" && <button type="button" className={styles.removeRecipient} onClick={() => removeExtraRecipient(recipient.email)} aria-label={`Remover ${recipient.email}`} title="Remover"><MdDeleteOutline /></button>}
                </div>)}
            </div>

            <FormGroup labelText="Adicionar e-mail somente neste envio">
                <div className={styles.extraRecipient}>
                    <Input type="email" placeholder="financeiro@empresa.com.br" value={extraEmail} onChange={event => setExtraEmail(event.target.value)} onKeyDown={event => { if (event.key === "Enter") { event.preventDefault(); addExtraEmail() } }} />
                    <Button type="button" variant="secondary" fullWidth={false} disabled={!hasValidExtraEmail} onClick={addExtraEmail}><MdAdd /> Adicionar</Button>
                </div>
            </FormGroup>

            <div className={styles.emailActions}>
                <span>{selectedRecipients.length} destinatário{selectedRecipients.length === 1 ? "" : "s"} selecionado{selectedRecipients.length === 1 ? "" : "s"}</span>
                <div><Button type="button" variant="secondary" fullWidth={false} onClick={closeModal}>Cancelar</Button><Button type="submit" variant="primary" fullWidth={false} disabled={!selectedRecipients.length}><MdSend /> Enviar agora</Button></div>
            </div>
        </form>
    </Modal>
}

export default ModalFinancialEmail
